import { useEffect, useRef } from 'react';

import { useStoreActions } from '~/store/hooks';
import { NormalizedTeamMembersAllocations } from '../../../shared/model/types';

type NewAddedClients = { teamId: number; userId: number; clientId: number };

interface IProps {
  teamMembersAllocations: NormalizedTeamMembersAllocations[];
  allocationDate: string;
  clinicId: string;
}

const useEditAllocationAvailability = ({
  teamMembersAllocations,
  allocationDate,
  clinicId,
}: IProps): void => {
  const { onValidateAvailability } = useStoreActions(actions => actions.clientAllocation);

  const newClients = teamMembersAllocations.flatMap(({ id: userId, clientsAllocations }) =>
    clientsAllocations.reduce<NewAddedClients[]>((acc, currentValue) => {
      if (currentValue.allocation === null)
        acc.push({ teamId: currentValue.team?.id, userId, clientId: currentValue.clientId });
      return acc;
    }, []),
  );

  const newClientsKey = newClients
    .map(({ userId, clientId, teamId }) => `${userId}:${clientId}:${teamId}`)
    .sort()
    .join(',');

  const prevKeyRef = useRef(newClientsKey);
  const prevDateRef = useRef(allocationDate);

  useEffect(() => {
    const contentChanged = newClientsKey !== prevKeyRef.current;
    const dateChanged = allocationDate !== prevDateRef.current;

    if (contentChanged || dateChanged) {
      onValidateAvailability({
        clinicId: Number(clinicId),
        payload: {
          dateRange: { from: allocationDate, to: allocationDate },
          clientAllocations: newClients,
        },
      });
    }

    prevKeyRef.current = newClientsKey;
    prevDateRef.current = allocationDate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newClientsKey, allocationDate]);
};

export default useEditAllocationAvailability;
